import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, CheckCircle, AlertCircle, Loader2, Trash2, Plus, Edit, RefreshCw, Search, Eye, EyeOff, Bell, Settings2, MapPin } from 'lucide-react';
import { format } from 'date-fns';

import { BACKEND_URL, API } from '../utils/apiConfig';
import { ConflictCard } from '../components/bookings/ConflictCard';

const PAYMENT_MODES = ['Cash', 'UPI', 'Online', 'Wallet'];
const BOOKING_SOURCES = ['Calling', 'Khelomore', 'District', 'Hudle', 'Walk-in', 'WhatsApp', 'App', 'Others'];

// 30-minute time slots (identical to /bookings/new)
const TIME_SLOTS = [];
for (let hour = 6; hour <= 26; hour++) {
  for (let minute = 0; minute < 60; minute += 30) {
    const totalMinutes = hour * 60 + minute;
    const displayHour = Math.floor(totalMinutes / 60);
    const displayMinute = totalMinutes % 60;
    const finalHour = displayHour > 23 ? displayHour - 24 : displayHour;
    const ampm = finalHour < 12 ? 'AM' : 'PM';
    const displayHour12 = finalHour === 0 ? 12 : finalHour > 12 ? finalHour - 12 : finalHour;
    TIME_SLOTS.push(`${String(displayHour12).padStart(2, '0')}:${String(displayMinute).padStart(2, '0')} ${ampm}`);
    if (displayHour >= 26) break;
  }
}

const getEndTimeAfter60Min = (startTime) => {
  const startIndex = TIME_SLOTS.indexOf(startTime);
  if (startIndex === -1) return '';
  const endIndex = startIndex + 2; // 60 min = 2 × 30-min slots
  return endIndex < TIME_SLOTS.length ? TIME_SLOTS[endIndex] : TIME_SLOTS[TIME_SLOTS.length - 1];
};

export default function EmailBookingPage() {
  const { admin } = useAuth();
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame, allGames } = useMasterData();
  const VENUES = getVenueNames();
  const ALL_GAMES = allGames.length > 0 ? allGames : ['Pickleball', 'Badminton', 'Cricket', 'Football Half', 'Football Full'];

  // Map a parsed/free-text value (e.g. "Bhayandar") to the canonical master option
  // (e.g. "MAKS Bhayandar") so the Edit dialog dropdowns pre-fill correctly.
  const resolveToOption = (value, options) => {
    if (!value || !options || options.length === 0) return value;
    const strip = (s) => String(s).toLowerCase().replace(/[\s\-\/]/g, '');
    const v = String(value).toLowerCase().trim();
    const exact = options.find((o) => String(o).toLowerCase() === v);
    if (exact) return exact;
    const vs = strip(value);
    const norm = options.find((o) => {
      const os = strip(o);
      return os === vs || os.startsWith(vs) || vs.startsWith(os);
    });
    if (norm) return norm;
    const partial = options.find((o) => {
      const ol = String(o).toLowerCase();
      return ol.includes(v) || v.includes(ol);
    });
    return partial || value;
  };

  const buildBookingFromParsed = (pd, slot) => {
    const venue = resolveToOption(pd.venue, VENUES);
    const game = resolveToOption(pd.game, getGamesForVenue(venue));
    const court = resolveToOption(pd.court || 'Court 1', getCourtsForVenueGame(venue, game));
    return {
      venue, game, court,
      date: pd.date || '',
      start_time: slot.start_time || '',
      end_time: slot.end_time || '',
      player_name: pd.player_name || '',
      player_mobile: pd.player_mobile || '',
      amount: slot.amount || 0,
      // Online platform bookings are always fully paid — no pending at venue
      advance_payment: (pd.payment_mode || 'Online') === 'Online' ? (slot.amount || 0) : 0,
      payment_mode: pd.payment_mode || 'Online',
      booking_source: pd.booking_source || 'Others',
      remarks: pd.remarks || '',
    };
  };
  
  const [emailBookings, setEmailBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showNewEmailForm, setShowNewEmailForm] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedEmailBooking, setSelectedEmailBooking] = useState(null);
  const [parsedBookings, setParsedBookings] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [createConflict, setCreateConflict] = useState(null);
  const [conflictSummary, setConflictSummary] = useState({ venues: {}, total: 0, player_names: [] });
  const [showConflictAlertsDialog, setShowConflictAlertsDialog] = useState(false);
  
  // Check if current user is admin
  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';

  useEffect(() => {
    fetchEmailBookings();
    fetchConflictSummary();
  }, []);

  const fetchConflictSummary = async () => {
    try {
      const response = await axios.get(`${API}/conflict-alerts/summary`);
      setConflictSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch conflict summary:', error);
    }
  };

  const fetchEmailBookings = async () => {
    try {
      const response = await axios.get(`${API}/email-bookings`);
      setEmailBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch email bookings:', error);
      toast.error('Failed to load email bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEmailBookings();
    await fetchConflictSummary();
    setRefreshing(false);
  };

  const handleDeleteEmailBooking = async (id) => {
    try {
      await axios.delete(`${API}/email-bookings/${id}`);
      toast.success('Email booking deleted');
      fetchEmailBookings();
    } catch (error) {
      toast.error('Failed to delete email booking');
    }
  };

  const [reparsingId, setReparsingId] = useState(null);
  const [healthFilter, setHealthFilter] = useState('all'); // 'all' | 'mismatch'
  const [statusFilter, setStatusFilter] = useState('unavailable');
  const [playDateFilter, setPlayDateFilter] = useState('next_15d'); // all|today|tomorrow|this_week|next_15d|custom
  const [playDateFrom, setPlayDateFrom] = useState('');
  const [playDateTo, setPlayDateTo] = useState('');
  const [hideDuplicate, setHideDuplicate] = useState(true);
  const [reviewReplace, setReviewReplace] = useState(false);

  const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Auto-Booked (Successful)' },
    { value: 'parsed', label: 'Parsed / Pending' },
    { value: 'unavailable', label: 'Unavailable (Conflicts)' },
    { value: 'duplicate', label: 'Duplicate' },
    { value: 'error', label: 'Failed / Error' },
    { value: 'skipped', label: 'Skipped / Cancelled' },
  ];

  const PLAY_DATE_FILTER_OPTIONS = [
    { value: 'all', label: 'All Playing Dates' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'this_week', label: 'This Week (Mon–Sun)' },
    { value: 'next_15d', label: 'Next 15 Days' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const playDateRange = () => {
    // Returns [fromISO, toISO] inclusive in YYYY-MM-DD, or null for 'all'
    if (playDateFilter === 'all') return null;
    const toISO = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (playDateFilter === 'today') return [toISO(today), toISO(today)];
    if (playDateFilter === 'tomorrow') {
      const t = new Date(today); t.setDate(t.getDate() + 1);
      return [toISO(t), toISO(t)];
    }
    if (playDateFilter === 'this_week') {
      // Mon = 1 … Sun = 7 (JS: 0 = Sun)
      const dow = today.getDay() || 7;
      const mon = new Date(today); mon.setDate(mon.getDate() - (dow - 1));
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
      return [toISO(mon), toISO(sun)];
    }
    if (playDateFilter === 'next_15d') {
      const end = new Date(today); end.setDate(end.getDate() + 15);
      return [toISO(today), toISO(end)];
    }
    if (playDateFilter === 'custom') {
      if (!playDateFrom && !playDateTo) return null;
      return [playDateFrom || '0000-01-01', playDateTo || '9999-12-31'];
    }
    return null;
  };

  const matchesPlayDateFilter = (eb) => {
    const range = playDateRange();
    if (!range) return true;
    const [from, to] = range;
    const d = eb?.parsed_data?.date;
    if (!d) return false;
    return d >= from && d <= to;
  };

  const matchesStatusFilter = (eb) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'unavailable') return Array.isArray(eb.conflict_slots) && eb.conflict_slots.length > 0;
    if (statusFilter === 'error') return eb.status === 'error' || eb.status === 'parsing_error';
    if (statusFilter === 'parsed') return ['parsed', 'pending', 'processing'].includes(eb.status);
    return eb.status === statusFilter;
  };

  // Re-parse: re-run parser and open an EDITABLE review form (manual-style) with fresh data
  const handleReparsePreview = async (eb) => {
    if (!eb.email_body) { toast.error('No stored email body to re-parse'); return; }
    setReparsingId(eb.id);
    try {
      const res = await axios.post(`${API}/email-bookings/${eb.id}/reparse-preview`);
      const pd = res.data.parsed_data || {};
      const timeSlots = pd.time_slots || [];
      const bookingsArray = timeSlots.map((slot) => buildBookingFromParsed(pd, slot));
      setSelectedEmailBooking(eb);
      setReviewReplace((eb.booking_ids || []).length > 0);
      setParsedBookings(bookingsArray);
      setShowReviewDialog(true);
      if (!timeSlots.length) toast.warning('Re-parsed but no time slots found — add manually.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Re-parse failed');
    } finally {
      setReparsingId(null);
    }
  };

  const handleReparseEmailBooking = async (eb) => {
    const oldCount = (eb.booking_ids || []).length;
    const confirmMsg = oldCount > 0
      ? `Re-parse this email with the latest parser?\n\nThis will DELETE the ${oldCount} existing booking(s) and recreate them from the freshly parsed data.\n\nContinue?`
      : `Re-parse this email with the latest parser?\n\nNo existing bookings will be deleted (none were created yet).`;
    if (!window.confirm(confirmMsg)) return;
    setReparsingId(eb.id);
    try {
      const res = await axios.post(`${API}/email-bookings/${eb.id}/reparse`);
      const { old_bookings_deleted = 0, new_booking_ids = [], skipped = [] } = res.data || {};
      if (new_booking_ids.length > 0) {
        // Booking(s) created successfully — show as normal, no conflict/skip warnings
        toast.success(`Re-parsed: created ${new_booking_ids.length} booking(s)`);
      } else if (skipped.length) {
        // Nothing created — surface why (overlapping slots)
        const description = skipped.slice(0, 3).map((s) => {
          const c = s.conflict || {};
          const bulkTag = c.is_bulk ? ' [BULK]' : '';
          if (c.player_name && c.player_name !== 'N/A') {
            return `${s.court} ${s.start_time}-${s.end_time}: clashes with ${c.player_name} • ${c.game} • ${c.booking_source}${bulkTag} • ID:${c.booking_id}`;
          }
          return `${s.court} ${s.start_time}-${s.end_time}: ${s.reason}`;
        }).join('\n');
        toast.message(`${skipped.length} slot(s) skipped — overlapping`, { description, duration: 12000 });
      } else {
        toast.success(`Re-parsed: deleted ${old_bookings_deleted}`);
      }
      fetchEmailBookings();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Re-parse failed';
      toast.error(msg);
    } finally {
      setReparsingId(null);
    }
  };

  const handleReviewEmailBooking = (emailBooking) => {
    setSelectedEmailBooking(emailBooking);
    setReviewReplace((emailBooking.booking_ids || []).length > 0);
    
    // Convert parsed data to booking format
    const timeSlots = emailBooking.parsed_data?.time_slots || [];
    const bookingsArray = timeSlots.map((slot) => buildBookingFromParsed(emailBooking.parsed_data || {}, slot));
    
    setParsedBookings(bookingsArray);
    setShowReviewDialog(true);
  };

  const handleConfirmBookings = async () => {
    setProcessing(true);
    setCreateConflict(null);
    try {
      const response = await axios.post(
        `${API}/email-bookings/${selectedEmailBooking.id}/create-bookings`,
        {
          email_booking_id: selectedEmailBooking.id,
          bookings_data: parsedBookings,
          replace: reviewReplace,
        }
      );
      
      toast.success(response.data.message);
      setShowReviewDialog(false);
      fetchEmailBookings();
    } catch (error) {
      console.error('Failed to create bookings:', error);
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'overlapping_booking') {
        setCreateConflict(detail.conflict);
        toast.error(detail.message || 'Time slot not available');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create bookings');
      }
    } finally {
      setProcessing(false);
    }
  };

  const updateParsedBooking = (index, field, value) => {
    setParsedBookings((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateParsedBookingFields = (index, fields) => {
    setParsedBookings((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...fields };
      return updated;
    });
  };

  const updateAllParsedPlayer = (name, mobile) => {
    setParsedBookings((prev) => prev.map((b) => ({ ...b, player_name: name, player_mobile: mobile })));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Pending' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Processing' },
      parsed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Parsed' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Auto-Booked' },
      duplicate: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Duplicate Email' },
      error: { bg: 'bg-red-100', text: 'text-red-700', label: 'Error' },
      parsing_error: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Parsing Error' },
      skipped: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Skipped (Cancelled)' },
    };
    
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  /**
   * Compute parser health for a row.
   *
   * District truncation symptom: slot_sum is much LESS than parsed total
   * (because whole slots are missing). Normal Khelomore/Hudle emails have
   * small differences from taxes/coupons (typically <10%). We only flag rows
   * where the relative diff is large enough to suggest missing slots, not
   * accounting noise.
   *
   *   ok        → diff ≤ 5% of total  (tax/coupon noise, or exact match)
   *   minor     → diff 5–20%          (visible delta but plausibly fees)
   *   mismatch  → diff > 20%          (truncation likely — Re-parse recommended)
   *   unknown   → not enough data to compute
   */
  const getParserHealth = (eb) => {
    const pd = eb.parsed_data || {};
    const slots = pd.time_slots || [];
    if (!slots.length) return { state: 'unknown', message: 'No time slots parsed yet' };
    const slotSum = slots.reduce((acc, s) => acc + (Number(s.amount) || 0), 0);
    const parsedTotal = Number(pd.total_amount) || 0;
    if (slotSum <= 0 || parsedTotal <= 0) {
      return { state: 'unknown', message: 'Slot or total amount missing — cannot validate' };
    }
    const diff = Math.abs(slotSum - parsedTotal);
    const ratio = diff / Math.max(slotSum, parsedTotal);
    if (ratio <= 0.05) {
      return {
        state: 'ok',
        message: `Slot sum ₹${slotSum.toFixed(0)} ≈ total ₹${parsedTotal.toFixed(0)} (within 5%) ✓`,
      };
    }
    if (ratio <= 0.20) {
      return {
        state: 'minor',
        message: `Slot sum ₹${slotSum.toFixed(0)} vs total ₹${parsedTotal.toFixed(0)} differs by ${(ratio * 100).toFixed(0)}%. Likely taxes / coupon — review if needed.`,
      };
    }
    return {
      state: 'mismatch',
      message: `Slot sum (₹${slotSum.toFixed(0)}) differs from total (₹${parsedTotal.toFixed(0)}) by ${(ratio * 100).toFixed(0)}%. Email may be truncated — try Re-parse.`,
    };
  };

  const getHealthBadge = (eb) => {
    const h = getParserHealth(eb);
    const styles = {
      ok: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Healthy', icon: '✓' },
      minor: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', label: 'Tax/Fee diff', icon: '•' },
      mismatch: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', label: 'Check Slots', icon: '⚠' },
      unknown: { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200', label: 'No data', icon: '–' },
    };
    const s = styles[h.state];
    return (
      <span
        title={h.message}
        data-testid={`health-${eb.id}`}
        data-health={h.state}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.bg} ${s.text} ${s.border}`}
      >
        <span aria-hidden>{s.icon}</span>
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="email-booking-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
              Email Bookings
            </h1>
            <p className="text-slate-600 mt-1">Parse and process booking emails automatically</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-email-bookings-button"
              title="Reload list"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant={hideDuplicate ? 'default' : 'outline'}
              onClick={() => setHideDuplicate((v) => !v)}
              className={hideDuplicate ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              data-testid="hide-duplicate-toggle"
              title="Hide duplicate emails"
            >
              {hideDuplicate ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {hideDuplicate ? 'Duplicates Hidden' : 'Show Duplicates'}
            </Button>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="email-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={playDateFilter} onValueChange={setPlayDateFilter}>
              <SelectTrigger className="w-[190px]" data-testid="email-play-date-filter">
                <SelectValue placeholder="Playing Date" />
              </SelectTrigger>
              <SelectContent>
                {PLAY_DATE_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {playDateFilter === 'custom' && (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={playDateFrom}
                  onChange={(e) => setPlayDateFrom(e.target.value)}
                  className="h-9 px-2 rounded-md border border-slate-200 text-sm"
                  data-testid="email-play-date-from"
                  aria-label="Playing date from"
                />
                <span className="text-slate-400 text-sm">–</span>
                <input
                  type="date"
                  value={playDateTo}
                  onChange={(e) => setPlayDateTo(e.target.value)}
                  className="h-9 px-2 rounded-md border border-slate-200 text-sm"
                  data-testid="email-play-date-to"
                  aria-label="Playing date to"
                />
              </div>
            )}
            <Button
              onClick={() => setShowNewEmailForm(true)}
              className="bg-primary hover:bg-primary-hover transition-colors duration-200"
              data-testid="paste-email-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Paste Email
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowConflictAlertsDialog(true)}
              data-testid="conflict-alerts-settings-button"
              title="Configure venue-wise conflict WhatsApp alerts"
            >
              <Bell className="w-4 h-4 mr-2" />
              Conflict Alerts
            </Button>
          </div>
        </div>

        {/* Venue-wise Conflict Monitoring widget */}
        <Card className="p-4 border-slate-200" data-testid="venue-conflict-widget">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <MapPin className="w-4 h-4 text-amber-600" />
              Venue-wise Conflicts (Unavailable Bookings)
            </div>
            <span className="text-xs text-slate-500" data-testid="conflict-total-count">
              Total: <b>{conflictSummary.total}</b>
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.keys(conflictSummary.venues || {}).length === 0 ? (
              <span className="text-sm text-slate-400" data-testid="no-conflicts-label">No current conflicts across any venue 🎉</span>
            ) : (
              Object.entries(conflictSummary.venues).map(([venue, count]) => (
                <span
                  key={venue}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-50 text-amber-800 border border-amber-200"
                  data-testid={`conflict-venue-chip-${venue}`}
                >
                  {venue} <b>– {count}</b>
                </span>
              ))
            )}
          </div>
        </Card>

        {/* Webhook URL moved to Settings → "Email Forwarding" section */}
        {/* Email History - Visible to all users */}
        {emailBookings.length === 0 ? (
          <Card className="p-12 text-center border-slate-200">
            <Mail className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p className="text-slate-500 mb-4">No email bookings yet</p>
            <p className="text-sm text-slate-600 mb-4">
              Set up email forwarding to automatically receive and parse booking emails.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => setShowNewEmailForm(true)}
                variant="outline"
                className="border-slate-200"
              >
                Paste Email Manually
              </Button>
              <Button
                onClick={() => window.open('/EMAIL_FORWARDING_SETUP.md', '_blank')}
                className="bg-primary hover:bg-primary-hover"
              >
                Setup Forwarding Instructions
              </Button>
            </div>
          </Card>
        ) : (
          (() => {
            const mismatchCount = emailBookings.filter((eb) => getParserHealth(eb).state === 'mismatch').length;
            let visibleBookings = healthFilter === 'mismatch'
              ? emailBookings.filter((eb) => getParserHealth(eb).state === 'mismatch')
              : emailBookings;
            visibleBookings = visibleBookings.filter(matchesStatusFilter);
            visibleBookings = visibleBookings.filter(matchesPlayDateFilter);
            if (hideDuplicate) {
              visibleBookings = visibleBookings.filter((eb) => eb.status !== 'duplicate' && !eb.is_duplicate);
            }
            return (
              <>
                {/* Parser health summary bar */}
                <Card
                  className={`p-3 border ${mismatchCount > 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}
                  data-testid="parser-health-summary"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-sm">
                      {mismatchCount > 0 ? (
                        <>
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                          <span className="text-amber-800">
                            <strong data-testid="health-mismatch-count">{mismatchCount}</strong>{' '}
                            row{mismatchCount === 1 ? '' : 's'} have a slot-amount mismatch — likely truncated emails. Click <em>Re-parse</em> to retry.
                          </span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-800">
                            All parsed rows look healthy (slot totals match parsed amount).
                          </span>
                        </>
                      )}
                    </div>
                    {mismatchCount > 0 && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={healthFilter === 'all' ? 'default' : 'outline'}
                          onClick={() => setHealthFilter('all')}
                          data-testid="health-filter-all"
                        >
                          All ({emailBookings.length})
                        </Button>
                        <Button
                          size="sm"
                          variant={healthFilter === 'mismatch' ? 'default' : 'outline'}
                          onClick={() => setHealthFilter('mismatch')}
                          data-testid="health-filter-mismatch"
                          className={healthFilter === 'mismatch' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                        >
                          Needs attention ({mismatchCount})
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
                <div className="grid gap-4">
                  {visibleBookings.map((eb) => (
              <Card key={eb.id} className="p-6 border-slate-200" data-testid={`email-booking-${eb.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <h3 className="font-semibold text-slate-900">{eb.email_subject}</h3>
                      {getStatusBadge(eb.status)}
                      {getHealthBadge(eb)}
                      {eb.edited_by && (
                        <span
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700"
                          data-testid={`edited-badge-${eb.id}`}
                        >
                          <Edit className="w-3 h-3" /> Edited
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-2">
                      From: {eb.email_from} | Date: {eb.email_date}
                      {eb.created_at && (
                        <> | Parsed: {new Date(eb.created_at).toLocaleString()}</>
                      )}
                      {eb.edited_by && (
                        <> | <span className="text-purple-600 font-medium">Edited by {eb.edited_by} on {eb.edited_at ? new Date(eb.edited_at).toLocaleString() : ''}</span></>
                      )}
                    </p>
                    
                    {eb.parsed_data && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                        <p className="font-medium text-slate-900 mb-1">Parsed Information:</p>
                        <div className="grid grid-cols-2 gap-2 text-slate-600">
                          <div>Player: {eb.parsed_data.player_name}</div>
                          <div>Mobile: {eb.parsed_data.player_mobile || 'N/A'}</div>
                          <div>Venue: {eb.parsed_data.venue}</div>
                          <div>Game: {eb.parsed_data.game}</div>
                          <div>Date: {eb.parsed_data.date}</div>
                          <div>Court: {eb.parsed_data.court || 'N/A'}</div>
                          {eb.parsed_data.external_booking_id && (
                            <div data-testid="parsed-booking-id">Booking ID: <span className="font-mono font-semibold text-slate-800">{eb.parsed_data.external_booking_id}</span></div>
                          )}
                          <div className="col-span-2">
                            Slots: {eb.parsed_data.time_slots?.length || 0}
                            {eb.parsed_data.time_slots?.length > 0 && (
                              <span className="ml-2 text-slate-700">
                                ({eb.parsed_data.time_slots.map((slot, idx) => (
                                  <span key={idx}>
                                    {slot.start_time} - {slot.end_time}
                                    {idx < eb.parsed_data.time_slots.length - 1 ? ', ' : ''}
                                  </span>
                                ))})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {eb.error_message && !(eb.booking_ids && eb.booking_ids.length > 0) && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        {eb.error_message}
                      </div>
                    )}
                    
                    {Array.isArray(eb.conflict_slots) && eb.conflict_slots.length > 0 && !(eb.booking_ids && eb.booking_ids.length > 0) && (
                      <div className="mt-3 space-y-2" data-testid={`conflict-slots-${eb.id}`}>
                        {eb.conflict_slots.map((cs, i) => (
                          <ConflictCard key={i} conflict={cs.conflict} time={cs.time} />
                        ))}
                      </div>
                    )}
                    
                    {eb.booking_ids && eb.booking_ids.length > 0 && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 inline mr-2" />
                        Created {eb.booking_ids.length} booking(s): {eb.booking_ids.join(', ')}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReparsePreview(eb)}
                      disabled={reparsingId === eb.id || !eb.email_body}
                      data-testid={`reparse-${eb.id}`}
                      title="Re-run the parser and open an editable form to review/correct before saving"
                    >
                      {reparsingId === eb.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="ml-1.5 hidden sm:inline">Re-parse</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReviewEmailBooking(eb)}
                      disabled={!eb.parsed_data}
                      data-testid={`edit-${eb.id}`}
                      title="Edit the parsed booking details manually"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="ml-1.5 hidden sm:inline">Edit</span>
                    </Button>
                    {eb.status !== 'completed' && eb.status !== 'skipped' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEmailBooking(eb.id)}
                        className="hover:bg-red-50 text-red-600"
                        data-testid={`delete-${eb.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
                </div>
              </>
            );
          })()
        )}
      </div>

      <NewEmailDialog
        open={showNewEmailForm}
        onClose={() => setShowNewEmailForm(false)}
        onSuccess={() => {
          setShowNewEmailForm(false);
          fetchEmailBookings();
        }}
      />

      <ReviewBookingDialog
        open={showReviewDialog}
        onClose={() => { setShowReviewDialog(false); setCreateConflict(null); }}
        emailBooking={selectedEmailBooking}
        bookings={parsedBookings}
        onUpdateBooking={updateParsedBooking}
        onUpdateBookingFields={updateParsedBookingFields}
        onUpdateAllPlayer={updateAllParsedPlayer}
        onConfirm={handleConfirmBookings}
        processing={processing}
        conflict={createConflict}
      />

      <ConflictAlertsDialog
        open={showConflictAlertsDialog}
        onClose={() => setShowConflictAlertsDialog(false)}
        isAdmin={isAdmin}
        onSent={fetchConflictSummary}
      />
    </DashboardLayout>
  );
}

function ConflictAlertsDialog({ open, onClose, isAdmin, onSent }) {
  const [settings, setSettings] = useState({ enabled: false, interval_minutes: 60, only_new: false });
  const [status, setStatus] = useState({ last_run_at: null, last_sent_at: null, last_sent_count: 0, last_sent_summary: {} });
  const [recipients, setRecipients] = useState([]);
  const [templateCfg, setTemplateCfg] = useState({ name: '', body: '', params: [], preview: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [recipientForm, setRecipientForm] = useState({ name: '', mobile: '', is_active: true });
  const [recipientEditing, setRecipientEditing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, recipientsRes, templateRes] = await Promise.all([
        axios.get(`${API}/conflict-alerts/settings`),
        axios.get(`${API}/conflict-alerts/recipients`),
        axios.get(`${API}/conflict-alerts/template-config`),
      ]);
      const { last_run_at, last_sent_at, last_sent_count, last_sent_summary, ...s } = settingsRes.data;
      setSettings(s);
      setStatus({ last_run_at, last_sent_at, last_sent_count, last_sent_summary });
      setRecipients(recipientsRes.data.recipients || []);
      setTemplateCfg(templateRes.data);
    } catch (error) {
      toast.error('Failed to load conflict alert settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchAll();
  }, [open]);

  const saveSettings = async (next) => {
    setSaving(true);
    try {
      await axios.put(`${API}/conflict-alerts/settings`, next);
      setSettings(next);
      toast.success('Settings updated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/conflict-alerts/template-config`, {
        name: templateCfg.name, body: templateCfg.body, params: templateCfg.params,
      });
      toast.success('Template updated');
      const res = await axios.get(`${API}/conflict-alerts/template-config`);
      setTemplateCfg(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/conflict-alerts/test`);
      if (res.data.skipped === 'no_conflicts') {
        toast.info('No current conflicts to alert about');
      } else {
        toast.success(`Sent to ${res.data.sent_to} recipient(s)`);
      }
      await fetchAll();
      onSent?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const saveRecipient = async () => {
    if (!recipientForm.name.trim() || !recipientForm.mobile.trim()) {
      toast.error('Name and mobile are required');
      return;
    }
    try {
      if (recipientEditing) {
        await axios.put(`${API}/conflict-alerts/recipients/${recipientEditing.id}`, recipientForm);
      } else {
        await axios.post(`${API}/conflict-alerts/recipients`, recipientForm);
      }
      setRecipientForm({ name: '', mobile: '', is_active: true });
      setRecipientEditing(null);
      const res = await axios.get(`${API}/conflict-alerts/recipients`);
      setRecipients(res.data.recipients || []);
      toast.success(recipientEditing ? 'Recipient updated' : 'Recipient added');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save recipient');
    }
  };

  const deleteRecipient = async (r) => {
    try {
      await axios.delete(`${API}/conflict-alerts/recipients/${r.id}`);
      setRecipients((prev) => prev.filter((x) => x.id !== r.id));
      toast.success('Recipient removed');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete recipient');
    }
  };

  const toggleRecipientActive = async (r) => {
    try {
      await axios.put(`${API}/conflict-alerts/recipients/${r.id}`, { ...r, is_active: !r.is_active });
      setRecipients((prev) => prev.map((x) => (x.id === r.id ? { ...x, is_active: !x.is_active } : x)));
    } catch (error) {
      toast.error('Failed to toggle recipient');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[88vh] overflow-y-auto" data-testid="conflict-alerts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-amber-600" />Conflict Monitoring & WhatsApp Alerts</DialogTitle>
          <p className="text-xs text-slate-500">Alerts recipients about unavailable/conflicting bookings, venue-wise, on a configurable interval.</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-5">
            {/* Enable + interval */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">Enable Conflict Alerts</p>
                  <p className="text-xs text-slate-500">Master switch for the scheduled WhatsApp alert</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(v) => saveSettings({ ...settings, enabled: v })}
                  data-testid="conflict-alerts-enabled-switch"
                  disabled={saving}
                />
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-sm whitespace-nowrap">Check every (minutes):</Label>
                <Input
                  type="number"
                  min="5"
                  className="w-24"
                  value={settings.interval_minutes}
                  onChange={(e) => setSettings((s) => ({ ...s, interval_minutes: Number(e.target.value) }))}
                  onBlur={() => saveSettings(settings)}
                  data-testid="conflict-alerts-interval-input"
                />
                <span className="text-xs text-slate-400">Default: 60</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">Only alert about NEW conflicts</p>
                  <p className="text-xs text-slate-500">Off = resend all unresolved conflicts every run. On = only conflicts not yet alerted.</p>
                </div>
                <Switch
                  checked={settings.only_new}
                  onCheckedChange={(v) => saveSettings({ ...settings, only_new: v })}
                  data-testid="conflict-alerts-only-new-switch"
                  disabled={saving}
                />
              </div>
              <div className="text-xs text-slate-500 pt-2 border-t" data-testid="conflict-alerts-last-status">
                Last run: {status.last_run_at ? new Date(status.last_run_at).toLocaleString() : 'Never'}
                {' · '}Last sent: {status.last_sent_at ? new Date(status.last_sent_at).toLocaleString() : 'Never'}
                {' · '}Last count: {status.last_sent_count || 0}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTest}
                disabled={testing}
                data-testid="conflict-alerts-send-test-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                Send Test Now
              </Button>
            </div>

            {/* Template */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-slate-800 flex items-center gap-2"><Settings2 className="w-4 h-4" />WABA Template</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Template Name</Label>
                  <Input
                    value={templateCfg.name}
                    onChange={(e) => setTemplateCfg((t) => ({ ...t, name: e.target.value }))}
                    data-testid="conflict-alerts-template-name"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Body ({'{{'}1{'}}'}=Name, {'{{'}2{'}}'}=Date+Venue Counts, {'{{'}3{'}}'}=Player Names)</Label>
                <Textarea
                  rows={3}
                  value={templateCfg.body}
                  onChange={(e) => setTemplateCfg((t) => ({ ...t, body: e.target.value }))}
                  data-testid="conflict-alerts-template-body"
                />
              </div>
              {templateCfg.preview && (
                <div className="p-2 bg-slate-50 rounded text-xs text-slate-600 whitespace-pre-wrap" data-testid="conflict-alerts-template-preview">
                  <b>Preview:</b> {templateCfg.preview}
                </div>
              )}
              <Button size="sm" onClick={saveTemplate} disabled={saving} data-testid="conflict-alerts-save-template-btn">
                Save Template
              </Button>
            </div>

            {/* Recipients master */}
            <div className="border rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-slate-800">Recipients Master</p>
              <div className="space-y-2">
                {recipients.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 p-2 border rounded-lg" data-testid={`conflict-recipient-row-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.mobile}</p>
                    </div>
                    <Switch checked={r.is_active} onCheckedChange={() => toggleRecipientActive(r)} data-testid={`conflict-recipient-active-${r.id}`} />
                    <Button variant="ghost" size="sm" onClick={() => { setRecipientEditing(r); setRecipientForm({ name: r.name, mobile: r.mobile, is_active: r.is_active }); }} data-testid={`conflict-recipient-edit-${r.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => deleteRecipient(r)} data-testid={`conflict-recipient-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {recipients.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No recipients configured yet</p>}
              </div>
              <div className="border-t pt-2 space-y-2">
                <p className="text-sm font-medium text-slate-700">{recipientEditing ? 'Edit recipient' : 'Add recipient'}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Name"
                    value={recipientForm.name}
                    onChange={(e) => setRecipientForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="conflict-recipient-name-input"
                  />
                  <Input
                    placeholder="Mobile"
                    value={recipientForm.mobile}
                    onChange={(e) => setRecipientForm((f) => ({ ...f, mobile: e.target.value }))}
                    data-testid="conflict-recipient-mobile-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveRecipient} data-testid="conflict-recipient-save-btn">
                    {recipientEditing ? 'Update' : 'Add'}
                  </Button>
                  {recipientEditing && (
                    <Button size="sm" variant="outline" onClick={() => { setRecipientEditing(null); setRecipientForm({ name: '', mobile: '', is_active: true }); }}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="conflict-alerts-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewEmailDialog({ open, onClose, onSuccess }) {
  const [emailBody, setEmailBody] = useState('');
  const [parsing, setParsing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setParsing(true);

    try {
      const response = await axios.post(`${API}/email-bookings/parse`, { email_body: emailBody });
      
      if (response.data.skipped) {
        toast.info(response.data.message);
      } else if (response.data.success) {
        toast.success(`${response.data.message} - Booking IDs: ${response.data.booking_ids?.join(', ')}`);
      } else {
        toast.error(response.data.message || 'Failed to process email');
      }
      
      onSuccess();
      setEmailBody('');
    } catch (error) {
      console.error('Failed to parse email:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse email');
    } finally {
      setParsing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Paste Booking Email</DialogTitle>
          <p className="text-sm text-slate-600 mt-2">
            Simply paste the email content below. Our AI will automatically extract all booking details.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email_body">Email Content</Label>
            <Textarea
              id="email_body"
              placeholder="Paste the entire email content here..."
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              required
              rows={16}
              data-testid="email-body-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-parse">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={parsing}
              className="bg-primary hover:bg-primary-hover"
              data-testid="parse-email-button"
            >
              {parsing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Parsing with AI...
                </>
              ) : (
                'Parse Email'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReviewBookingDialog({ open, onClose, emailBooking, bookings, onUpdateBooking, onUpdateBookingFields, onUpdateAllPlayer, onConfirm, processing, conflict }) {
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame } = useMasterData();
  const VENUES = getVenueNames();
  const [players, setPlayers] = React.useState([]);
  const [playerSearch, setPlayerSearch] = React.useState('');
  const [showResults, setShowResults] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/contacts`);
        setPlayers((res.data || []).map((c) => ({
          id: c.player_mobile || c.player_name,
          name: c.player_name,
          mobile: c.player_mobile || '',
        })));
      } catch (e) {
        try {
          const r = await axios.get(`${API}/players`);
          setPlayers(r.data || []);
        } catch (_) { /* ignore */ }
      }
    })();
  }, [open]);

  const searchResults = playerSearch.length >= 2
    ? players.filter((p) =>
        (p.name && p.name.toLowerCase().includes(playerSearch.toLowerCase())) ||
        (p.mobile && p.mobile.includes(playerSearch))
      ).slice(0, 10)
    : [];

  if (!emailBooking || !bookings) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[960px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review & Edit Bookings</DialogTitle>
          <p className="text-sm text-slate-600 mt-2">
            Same fields as a manual booking — review and correct everything before saving.
          </p>
        </DialogHeader>

        {/* Player search — fills name & mobile across all slots */}
        <div className="space-y-2 relative">
          <Label>Search Existing Player</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name or mobile"
              value={playerSearch}
              onChange={(e) => { setPlayerSearch(e.target.value); setShowResults(true); }}
              className="pl-10"
              data-testid="review-player-search"
            />
          </div>
          {showResults && searchResults.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
              {searchResults.map((p, i) => (
                <button
                  key={p.id || i}
                  type="button"
                  onClick={() => { onUpdateAllPlayer(p.name, p.mobile); setPlayerSearch(p.name); setShowResults(false); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                  data-testid={`review-player-option-${i}`}
                >
                  <p className="font-medium text-slate-900">{p.name}</p>
                  <p className="text-sm text-slate-600">{p.mobile || 'No mobile'}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6 mt-2">
          {bookings.map((booking, index) => {
            const total = parseFloat(booking.amount) || 0;
            const isOnline = (booking.payment_mode || 'Online') === 'Online';
            // Online platform bookings are always fully paid; never show pending
            const advance = isOnline ? total : (parseFloat(booking.advance_payment) || 0);
            const pending = isOnline ? 0 : Math.max(0, total - advance);
            return (
            <Card key={index} className="p-4 border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">
                Booking #{index + 1} — {booking.start_time || '—'} to {booking.end_time || '—'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Venue */}
                <div className="space-y-2">
                  <Label>Venue</Label>
                  <Select value={booking.venue} onValueChange={(v) => onUpdateBookingFields(index, { venue: v, game: '', court: '' })}>
                    <SelectTrigger data-testid={`review-venue-${index}`}><SelectValue placeholder="Select venue" /></SelectTrigger>
                    <SelectContent>
                      {VENUES.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Game */}
                <div className="space-y-2">
                  <Label>Game</Label>
                  <Select value={booking.game} onValueChange={(v) => onUpdateBookingFields(index, { game: v, court: '' })} disabled={!booking.venue}>
                    <SelectTrigger data-testid={`review-game-${index}`}><SelectValue placeholder="Select game" /></SelectTrigger>
                    <SelectContent>
                      {getGamesForVenue(booking.venue).map((g) => (<SelectItem key={g} value={g}>{g}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Court */}
                <div className="space-y-2">
                  <Label>Court</Label>
                  <Select value={booking.court} onValueChange={(v) => onUpdateBooking(index, 'court', v)} disabled={!booking.game}>
                    <SelectTrigger data-testid={`review-court-${index}`}><SelectValue placeholder="Select court" /></SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const opts = (booking.venue && booking.game) ? getCourtsForVenueGame(booking.venue, booking.game) : [];
                        const courtOpts = booking.court && !opts.includes(booking.court) ? [booking.court, ...opts] : opts;
                        return courtOpts.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>));
                      })()}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={booking.date} onChange={(e) => onUpdateBooking(index, 'date', e.target.value)} data-testid={`review-date-${index}`} />
                </div>
                {/* Start Time */}
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Select
                    value={booking.start_time}
                    onValueChange={(v) => onUpdateBookingFields(index, { start_time: v, end_time: getEndTimeAfter60Min(v) })}
                  >
                    <SelectTrigger data-testid={`review-start-time-${index}`}><SelectValue placeholder="Select start time" /></SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {/* End Time */}
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Select value={booking.end_time} onValueChange={(v) => onUpdateBooking(index, 'end_time', v)} disabled={!booking.start_time}>
                    <SelectTrigger data-testid={`review-end-time-${index}`}><SelectValue placeholder="Select end time" /></SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.filter((t) => TIME_SLOTS.indexOf(t) > TIME_SLOTS.indexOf(booking.start_time)).map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Player Name */}
                <div className="space-y-2">
                  <Label>Player Name</Label>
                  <Input value={booking.player_name} onChange={(e) => onUpdateBooking(index, 'player_name', e.target.value)} data-testid={`review-player-name-${index}`} />
                </div>
                {/* Mobile */}
                <div className="space-y-2">
                  <Label>Mobile</Label>
                  <Input value={booking.player_mobile} onChange={(e) => onUpdateBooking(index, 'player_mobile', e.target.value)} data-testid={`review-player-mobile-${index}`} />
                </div>
                {/* Booking Source */}
                <div className="space-y-2">
                  <Label>Booking Source</Label>
                  <Select value={booking.booking_source} onValueChange={(v) => onUpdateBooking(index, 'booking_source', v)}>
                    <SelectTrigger data-testid={`review-source-${index}`}><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      {BOOKING_SOURCES.map((bs) => (<SelectItem key={bs} value={bs}>{bs}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Total Amount */}
                <div className="space-y-2">
                  <Label>Total Amount (₹)</Label>
                  <Input type="number" min="0" value={booking.amount} onChange={(e) => onUpdateBooking(index, 'amount', e.target.value)} data-testid={`review-amount-${index}`} />
                </div>
                {/* Advance Payment — hidden for Online (always fully paid) */}
                {!isOnline && (
                <div className="space-y-2">
                  <Label>Advance Payment (₹)</Label>
                  <Input type="number" min="0" value={booking.advance_payment || ''} onChange={(e) => onUpdateBooking(index, 'advance_payment', e.target.value)} data-testid={`review-advance-${index}`} />
                </div>
                )}
                {/* Payment Mode */}
                <div className="space-y-2">
                  <Label>Payment Mode</Label>
                  <Select value={booking.payment_mode} onValueChange={(v) => onUpdateBooking(index, 'payment_mode', v)}>
                    <SelectTrigger data-testid={`review-payment-mode-${index}`}><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map((pm) => (<SelectItem key={pm} value={pm}>{pm}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pending display */}
                {total > 0 && (
                  <div className="md:col-span-3">
                    <div className={`p-3 rounded-lg border flex items-center justify-between ${pending > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                      <span className={`text-sm font-medium ${pending > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                        {pending > 0 ? 'Pending to Collect at Venue' : 'Fully Paid'}
                      </span>
                      <span className={`font-bold text-lg ${pending > 0 ? 'text-amber-700' : 'text-green-700'}`} data-testid={`review-pending-${index}`}>
                        ₹{pending.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                )}

                {/* Remarks */}
                <div className="space-y-2 md:col-span-3">
                  <Label>Remarks (Optional)</Label>
                  <Textarea rows={2} value={booking.remarks || ''} onChange={(e) => onUpdateBooking(index, 'remarks', e.target.value)} data-testid={`review-remarks-${index}`} />
                </div>
              </div>
            </Card>
          );
          })}
        </div>

        {conflict && (
          <div className="mt-2" data-testid="review-conflict">
            <ConflictCard
              conflict={conflict}
              title="Slot not available"
              onNavigate={onClose}
            />
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-confirm">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={processing}
            className="bg-primary hover:bg-primary-hover"
            data-testid="confirm-bookings-button"
          >
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              `Confirm & Create ${bookings.length} Booking(s)`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
